import { InfoTooltip } from '../InfoTooltip.js';
import { METRIC_EXPLANATIONS } from '../../data/metric-explanations.js';
import { formatScoreWithLabel } from '../../lib/scoreFormatter.js';

export interface SocialIntelData {
  websiteExists: boolean;
  websiteAgeDays: number | null;
  websiteStatus: string | null;
  twitterExists: boolean;
  twitterHandle: string | null;
  twitterFollowers: number | null;
  twitterActivityLevel: string;
  telegramExists: boolean;
  telegramMembers: number | null;
  githubExists: boolean;
  githubCommits30d: number | null;
  socialTrustLevel: string;
  socialTrustScore: number;
  dataUnavailableReason?: string;
}

const FIELD_UNAVAILABLE = 'Not enough public data yet';

function trustColor(level: string): string {
  if (level === 'HIGH') return '#27ae60';
  if (level === 'MEDIUM') return '#f39c12';
  if (level === 'LOW') return '#e67e22';
  return '#888';
}

export function SocialIntelPanel({ data }: { data: SocialIntelData | null | undefined }) {
  const d = data ?? null;

  if (!d) return null;

  const noneExist = !d.websiteExists && !d.twitterExists && !d.telegramExists && !d.githubExists;

  if (noneExist && d.dataUnavailableReason) {
    return (
      <div className="dash-card ws-forensic-section">
        <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          Social &amp; Web Presence
          <InfoTooltip explain={METRIC_EXPLANATIONS.socialPresence} compact />
        </h3>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Social Presence</span>
            <span className="intel-metric-value" style={{ fontSize: '0.85rem' }}>
              {d.dataUnavailableReason || FIELD_UNAVAILABLE}
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="dash-card ws-forensic-section">
      <h3 className="ws-forensic-title" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        Social &amp; Web Presence
        <InfoTooltip explain={METRIC_EXPLANATIONS.socialPresence} compact />
      </h3>
      <div className="intel-grid intel-grid-3">
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Website</span>
            <span className={`intel-bool-badge ${d.websiteExists ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.websiteExists ? 'Found' : 'Not Found'}
            </span>
            {d.websiteExists && d.websiteStatus && (
              <span className="intel-metric-value" style={{ fontSize: '0.7rem', marginTop: '0.2rem', textTransform: 'capitalize' }}>
                {d.websiteStatus}
              </span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Twitter / X</span>
            <span className={`intel-bool-badge ${d.twitterExists ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.twitterExists ? 'Found' : 'Not Found'}
            </span>
            {d.twitterHandle && (
              <span className="intel-metric-value" style={{ fontSize: '0.7rem', marginTop: '0.2rem' }}>
                @{d.twitterHandle}
              </span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Telegram</span>
            <span className={`intel-bool-badge ${d.telegramExists ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.telegramExists ? 'Found' : 'Not Found'}
            </span>
            {d.telegramMembers != null && (
              <span className="intel-metric-value" style={{ fontSize: '0.7rem', marginTop: '0.2rem' }}>
                {d.telegramMembers.toLocaleString('en-US')} members
              </span>
            )}
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">GitHub</span>
            <span className={`intel-bool-badge ${d.githubExists ? 'intel-bool-good' : 'intel-bool-bad'}`}>
              {d.githubExists ? 'Found' : 'Not Found'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">
              Social Trust
              <InfoTooltip explain={METRIC_EXPLANATIONS.socialPresence} compact />
            </span>
            <span className={`intel-bool-badge`} style={{ backgroundColor: trustColor(d.socialTrustLevel), color: '#fff' }}>
              {d.socialTrustLevel === 'HIGH' ? 'Strong' : d.socialTrustLevel === 'MEDIUM' ? 'Moderate' : d.socialTrustLevel === 'LOW' ? 'Weak' : 'Unknown'}
            </span>
          </div>
        </div>
        <div className="intel-metric-card card">
          <div className="intel-metric">
            <span className="intel-metric-label">Social Trust Score</span>
            <span className="intel-metric-value" style={{ color: trustColor(d.socialTrustLevel) }}>
              {formatScoreWithLabel(d.socialTrustScore)}
            </span>
          </div>
        </div>
      </div>
      {d.dataUnavailableReason && (
        <p style={{ margin: '0.5rem 0 0', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
          {d.dataUnavailableReason}
        </p>
      )}
    </div>
  );
}
